import React, { useState } from "react";
import TableTransactionWorkflow from "../TableTransactionWorkflow/TableTransactionWorkflow";
import { ReactComponent as FileIcon } from "../../../assets/img/fileIcoon.svg";
import { ReactComponent as PayIcon } from "../../../assets/img/payIcon.svg";
import { WALLET_TRANSACTION_STATUS } from "../../Utils/constants";
import ModalContainer from "../../Modals/CustomModal/Modal";
import Pagination from "../../Pagination/Pagination";
import { ReactComponent as RecheckIcon } from "../../../assets/img/recheckIcon.svg";
import { MobileTableView } from "../../Table/CustomTable/MobileTable";
import { MobileWalletCard } from "./WalletTableMobile";
import DesktopWallet from "./DesktopWallet";
import useIsMobile from "../../../hooks/useIsMobile";
import BottomSheet from "../../BottomSheet/BottomSheet";
import { downloadInvoiceHandler } from "../../../helper/downloadHandler";
import { toast } from "react-toastify";
import { getTransactionId, verifyAndFundWallet } from "../../../services/selfService";
import { remitaPaymentService } from "../../../services/payment";
import { useFundWalletStore } from "../../../store/fundWallet";
import { ErrorPaymentCard } from "../../BuyPowerWorkFlow/PaymentStatusCard";
import FundWalletSuccessStep from "../../FundWalletWorkFlow/SuccessStep";
import { usePowerPurchaseStore } from "../../../store/buyPowerStore";

interface WalletTableProps {
  walletDataHook: ReturnType<typeof import("../../../hooks/useWalletData").useWalletData>;
  showViewAll?: boolean;
}

const WalletTable: React.FC<WalletTableProps> = ({ walletDataHook, showViewAll }) => {
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<any>(null);
  const { setPaymentResult, setLoading } = useFundWalletStore();
  const {triggerRefetch} = usePowerPurchaseStore();
  const [open, setOpen] = useState(false);
  const isMobile = useIsMobile();
  const [downloadingReceipt, setDownloadingReceipt] = useState(false);
  const { data, loading, error, totalCount, currentPage, pageSize, handlePageChange, refetch } = walletDataHook;
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);

  const handleRecheck = async (selectedTransaction: any) => {
    try {
      const remitaResponseData = selectedTransaction.remitaResponse || "anything";
      // Verify payment on backend
      let payload = {
        transactionId: selectedTransaction.transactionId as string,
        amount: selectedTransaction.amount,

        invoiceNumber: selectedTransaction.invoiceNumber,
        remitaPaymentResponse: remitaResponseData,
      };

      const verificationResponse = await verifyAndFundWallet({
        ...payload,
      });

      if (verificationResponse.data.isSuccessful && verificationResponse.data.data) {
        toast.success("Recheck successful! Payment status updated.");
        refetch();
        hideAction();
      }
    } catch (error: any) {
      const errorMessage = error?.data?.message || error.message;
      toast.error(errorMessage || "An error occurred during recheck.");
    }
  };

  const handlePayOnline = async (selectedTransaction: any) => {
    try {
      let payload = {
        transactionId: selectedTransaction.transactionId as string,
        amount: selectedTransaction.amount,
        // meterNo: meterDetails.meterNumber,
        // paymentMethod: PaymentMethod.Remita,
        invoiceNumber: selectedTransaction.invoiceNumber,
        remitaPaymentResponse: "",
      };

      const transactionIdResponse = await getTransactionId({
        invoiceNumber: selectedTransaction.invoiceNumber,
        transactionId: selectedTransaction?.transactionId as string,
      });
      if (!transactionIdResponse?.data?.isSuccessful || !transactionIdResponse?.data?.data) {
        toast.error("Failed to generate transaction ID. Please try again.");
        setIsProcessingPayment(false);

        return;
      }

      const transactionId = transactionIdResponse.data.data;

      payload.transactionId = transactionId;

      // Initialize Remita payment

      await remitaPaymentService.initializePayment(
        {
          key: "", // From env
          rrr: selectedTransaction.rrr,
          transactionId,
          amount: selectedTransaction.amount,
        },
        {
          onSuccess: async (remitaResponse) => {
            const remitaResponseData = remitaResponse ? JSON.stringify(remitaResponse) : "anything";
            // Verify payment on backend AND call verify fund wallet
            payload.remitaPaymentResponse = remitaResponseData;
            const verificationResponse = await verifyAndFundWallet({
              ...payload,
              // remitaPaymentResponse: remitaResponseData,
            });

            if (verificationResponse.data.isSuccessful && verificationResponse.data.data) {
              const verificationResponseData = verificationResponse.data.data;
              setPaymentResult({
                amount: verificationResponseData.amount,
              });

              refetch();
              setShowSuccessModal(true);
              toast.success("Payment successful!");
            } else {
              toast.error("Payment verification failed. Please contact support.");
              setShowErrorModal(true);
            }
            triggerRefetch();
          },
          onError: async (remitaResponse) => {
            toast.error(remitaResponse.message || "Payment failed");
            setIsProcessingPayment(false);
            const remitaResponseData = remitaResponse ? JSON.stringify(remitaResponse) : "anything";
            // Verify payment on backend
            payload.remitaPaymentResponse = remitaResponseData;
            const verificationResponse = await verifyAndFundWallet({
              ...payload,
              // remitaPaymentResponse: remitaResponseData,
            });
            setShowErrorModal(true);
          },

          onClose: () => {
            setIsProcessingPayment(false);
            setLoading(false);
          },
        }
      );
    } catch (error: any) {
      const errorMessage = error.data?.message || error.message;
      toast.error(errorMessage || "Failed to initialize payment");
    } finally {
      setIsProcessingPayment(false);
      setLoading(false);
    }
  };

  const hideAction = () => {
    setShowTransactionModal(false);
    setSelectedTransaction(null);
  };

  const tableActions = [
    {
          title: "Recheck",
          allowOnly: WALLET_TRANSACTION_STATUS.PENDING,
          action: () => {
            // hideAction();
            handleRecheck(selectedTransaction);
          },
          icon: <RecheckIcon />,
        },
    {
      title: "Make Payment",
      allowOnly: WALLET_TRANSACTION_STATUS.UNPAID,
      action: () => {
        hideAction();
        handlePayOnline(selectedTransaction);
      },
      icon: <PayIcon />,
    },
    {
      title: "Download Invoice",
      allowOnly: WALLET_TRANSACTION_STATUS.PAID,
      action: async () => {
        await downloadInvoiceHandler(selectedTransaction?.invoiceNumber, {
          onStart: () => setDownloadingReceipt(true),
          onFinally: () => {
            setDownloadingReceipt(false);
            hideAction();
          },
        });
      },
      icon: <FileIcon />,
    },
  ];

  const handleTableActionClick = (item: any) => {
    setSelectedTransaction(item);
    setShowTransactionModal(true);
  };
  // Loading state
  if (loading && currentPage === 1) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading transactions...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button onClick={() => refetch()} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Empty state
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-gray-500 mb-2">No data found</p>
        </div>
      </div>
    );
  }
  const mobileView = (
    <>
      <MobileTableView
        data={data || []}
        renderCard={(item: any) => (
          <MobileWalletCard key={item.id} data={item} onClick={() => handleTableActionClick(item)} />
        )}
        emptyMessage="No wallet transactions found"
      />
      {totalCount > pageSize && (
        <div className="w-full my-4">
          <Pagination
            totalItems={totalCount}
            itemsPerPage={pageSize || 10}
            onPageChange={handlePageChange}
            currentPage={currentPage}
          />
        </div>
      )}
    </>
  );

  return (
    <div>
      {/* Mobile View */}
      <div className={`block md:hidden`}>{mobileView}</div>

      <DesktopWallet
        data={data || []}
        currentPageNumber={currentPage}
        open={open}
        setOpen={setOpen}
        pageSize={pageSize}
        totalCount={totalCount}
        selectedIconIndex={null}
        setSelectedTransaction={setSelectedTransaction}
        setShowTransactionModal={setShowTransactionModal}
        handleTableActionClick={handleTableActionClick}
        handlePageChange={handlePageChange}
        showViewAll={showViewAll}
      />

      {!isMobile && (
        <>
          <ModalContainer width={600} showModal={showTransactionModal} setShowModal={setShowTransactionModal}>
            <TableTransactionWorkflow
              selectedTransaction={selectedTransaction}
              hideAction={() => {
                setShowTransactionModal(false);
                setSelectedTransaction(null);
              }}
              tableActions={tableActions}
              type="wallet"
            />
          </ModalContainer>
          <ModalContainer
            showModal={showSuccessModal}
            showGradient
            variant="success"
            setShowModal={setShowSuccessModal}
            width={600}
          >
            <FundWalletSuccessStep
              title="Funding successful"
              message=""
              primaryButtonText="Close"
              secondaryButtonText="Try again"
              onPrimaryClick={() => setShowSuccessModal(false)}
              onClose={() => setShowSuccessModal(false)}
            />{" "}
          </ModalContainer>

          {/* Error Modal - separate */}
          <ModalContainer
            showModal={showErrorModal}
            showGradient
            variant="error"
            setShowModal={setShowErrorModal}
            width={600}
          >
            <ErrorPaymentCard
              title="Error processing payment"
              message="We couldn't process your payment."
              primaryButtonText="Try again"
              secondaryButtonText="Close"
              onPrimaryClick={() => console.log("retry")}
              onSecondaryClick={() => console.log("close")}
              onClose={() => console.log("close")}
            />
          </ModalContainer>
        </>
      )}
      {isMobile && (
        <>
          <BottomSheet
            openBottomSheet={showTransactionModal}
            setOpenBottomSheet={setShowTransactionModal}
            onCloseAction={() => setShowTransactionModal(false)}
            showGradient
            variant="success"
          >
            <TableTransactionWorkflow
              selectedTransaction={selectedTransaction}
              tableActions={tableActions}
              hideAction={() => {
                setOpen(false);
                setSelectedTransaction(null);
              }}
              type="wallet"
            />
          </BottomSheet>
          <BottomSheet
            title={""}
            openBottomSheet={showSuccessModal}
            setOpenBottomSheet={setShowSuccessModal}
            onCloseAction={() => {
              setShowSuccessModal(false);
            }}
            showGradient
            variant="success"
          >
            <FundWalletSuccessStep
              title="Funding successful"
              message=""
              primaryButtonText="Close"
              secondaryButtonText="Try again"
              onPrimaryClick={() => setShowSuccessModal(false)}
              onClose={() => setShowSuccessModal(false)}
            />{" "}
          </BottomSheet>

          {/* // Error Modal - separate */}
          <BottomSheet
            title={""}
            openBottomSheet={showErrorModal}
            setOpenBottomSheet={setShowErrorModal}
            onCloseAction={() => {
              setShowErrorModal(false);
            }}
            showGradient
            variant="error"
          >
            <ErrorPaymentCard
              title="Error processing payment"
              message="We couldn't process your payment."
              primaryButtonText="Try again"
              secondaryButtonText="Close"
              onPrimaryClick={() => console.log("retry")}
              onSecondaryClick={() => console.log("close")}
              onClose={() => console.log("close")}
            />
          </BottomSheet>
        </>
      )}
    </div>
  );
};

export default WalletTable;
